import { Button, ButtonMode, ButtonSize, Loader, Tooltip, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import useFetchAnalysts from '../../../hooks/analyst/useFetchAnalysts';
import { useBreakpoint } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { useToast } from '../../../hooks/toast/useToast';
import { WIDGET_ID } from '../../../pages/Article/Article.constants';
import { hasPermissionToAskAnAnalyst } from '../../../utils/permissionsUtils';
import { useGlobalModalContext } from '../../GlobalModal';
import { MODAL_TYPES } from '../../GlobalModal/GlobalModal';
import AnalystsContent from './AnalystsContent';
import styles from './AnalystsWidget.module.scss';

const cx = classNames.bind(styles);

export interface AskAnalystModalSubject {
  articleId?: number;
  analystId?: string;
  companyId?: string;
  subjectName: string;
}

interface AnalystsWidgetProps {
  analysts: string[];
  print?: boolean;
  minimized?: boolean;
  setIsAnalystsWidgetRendered?: (value: boolean) => void;
  hideDeactivatedAnalyst?: boolean;
  canAccess?: boolean;
  askAnalystModalSubject?: AskAnalystModalSubject;
  setIsArticleAnalystWidgetLoading?: (value: boolean) => void;
}

const renderAskAnAnalyst = (onAskAnalystClick, buttonMode = ButtonMode.PRIMARY, size = ButtonSize.SMALL) => (
  <Button mode={buttonMode} size={size} onClick={onAskAnalystClick} dataTestId="analyst-button">
    Ask an Analyst
  </Button>
);

const AnalystsWidget = ({
  analysts,
  print,
  minimized,
  setIsAnalystsWidgetRendered,
  hideDeactivatedAnalyst,
  canAccess,
  askAnalystModalSubject,
  setIsArticleAnalystWidgetLoading,
}: AnalystsWidgetProps): React.ReactElement | null => {
  const { data, isLoading } = useFetchAnalysts({ analystIds: analysts, enabled: canAccess });
  const { data: userPermissions } = useFetchUserPermissions();
  const { isTabletPortrait } = useBreakpoint();
  const { showModal, setError, closeModal, store } = useGlobalModalContext();
  const { showToast } = useToast();

  const canAskAnAnalyst = hasPermissionToAskAnAnalyst(userPermissions!);

  const isAllAnalystsDeactivated = !data
    ?.map((analyst) => {
      return analyst.isActiveAuthor;
    })
    .includes(true);

  const onAnalystClick = () => {
    showModal(MODAL_TYPES.ANALYST, { data, hideDeactivatedAnalyst, onAskAnalystClick, canAskAnAnalyst });
  };

  const onAskAnalystModalSuccess = () => {
    return showToast(<span>Message Sent</span>);
  };

  const setAskAnalystModalError = (value: string) => {
    return setError(value);
  };

  const onAskAnalystClick = () => {
    showModal(MODAL_TYPES.ASK_ANALYST, {
      subject: askAnalystModalSubject,
      onSuccess: onAskAnalystModalSuccess,
      setError: setAskAnalystModalError,
      error: store.props?.error,
      closeModal,
    });
  };

  useEffect(() => {
    if (setIsAnalystsWidgetRendered) {
      setIsAnalystsWidgetRendered(!isEmpty(data) && !isAllAnalystsDeactivated);
    }
  }, [data]);

  useEffect(() => {
    setIsArticleAnalystWidgetLoading?.(isLoading);
  }, [isLoading]);

  if ((isEmpty(data) && !isLoading) || (hideDeactivatedAnalyst && isAllAnalystsDeactivated)) {
    return null;
  }

  if (print) {
    return renderPrintView(data);
  }

  return minimized || isTabletPortrait
    ? renderTabletPortraitView(
        data,
        isLoading,
        onAnalystClick,
        onAskAnalystClick,
        minimized,
        hideDeactivatedAnalyst,
        canAskAnAnalyst
      )
    : renderDesktopAndLandscapeView(data, onAskAnalystClick, isLoading, canAskAnAnalyst);
};

const renderDesktopAndLandscapeView = (data, onAskAnalystClick, isLoading, canAskAnAnalyst) => {
  return (
    <div className={cx('body')} data-testid={WIDGET_ID.ANALYSTS}>
      <Widget
        headerTitle="Analysts"
        fixedHeight
        isLoading={isLoading}
        footer={canAskAnAnalyst && renderAskAnAnalyst(onAskAnalystClick)}
      >
        {data ? <AnalystsContent data={data} /> : <div>Analysts not found</div>}
      </Widget>
    </div>
  );
};

const renderTabletPortraitView = (
  data,
  isLoading,
  onAnalystClick,
  onAskAnalystClick,
  minimized,
  hideDeactivatedAnalyst,
  canAskAnAnalyst
) => {
  if (isLoading) {
    return <Loader text={null} />;
  }

  const MAX_RENDERED_ANALYSTS = 4;

  const filteredAnalysts = hideDeactivatedAnalyst
    ? data.filter((analyst) => {
        return analyst.isActiveAuthor === true;
      })
    : data;

  return (
    <div className={cx('body', 'no-print')} data-testid={WIDGET_ID.ANALYSTS}>
      <div className={cx('analyst-section-layout')}>
        <span className={cx('analysts-text-tablet-portrait')}>Analysts:</span>
        <div className={cx('analyst-margin')} onClick={onAnalystClick}>
          {filteredAnalysts.slice(0, MAX_RENDERED_ANALYSTS).map((analyst, i) => (
            <Tooltip content={analyst.displayName} key={`${analyst.initials}${analyst.displayName + i}`}>
              <div
                style={{ zIndex: `${filteredAnalysts.length - i}` }}
                className={cx('analysts-center-tablet')}
                data-testid="analyst-div"
              >
                {analyst.isActiveAuthor && analyst.imageUrl ? (
                  <img alt="" src={analyst.imageUrl} className={cx('initials-image-bg', 'analyst-image')} />
                ) : (
                  <span className={cx('initials', 'analyst-image')}>{analyst.initials.toUpperCase()} </span>
                )}
              </div>
            </Tooltip>
          ))}
          {filteredAnalysts.length > MAX_RENDERED_ANALYSTS && (
            <span className={cx('initials', 'analyst-image')} data-testid="additional-analyst-div">
              {`+${filteredAnalysts.length - MAX_RENDERED_ANALYSTS}`}
            </span>
          )}
        </div>
        {canAskAnAnalyst &&
          renderAskAnAnalyst(
            onAskAnalystClick,
            minimized ? ButtonMode.SECONDARY : ButtonMode.PRIMARY,
            hideDeactivatedAnalyst ? ButtonSize.MEDIUM : ButtonSize.SMALL
          )}
      </div>
    </div>
  );
};

const renderPrintView = (data) => (
  <div className={cx('body')} data-testid="article-analyst-print-view">
    <span className={cx('analysts-text')}>Analysts:</span>
    {data &&
      data.map((analyst, i) => (
        <div key={`${analyst.displayName + i}`} className={cx('analyst-print')}>
          <span>{analyst.displayName}</span>
          {analyst.role && <span className={cx('analyst-print-subtitle')}>{analyst.role}</span>}
        </div>
      ))}
  </div>
);

export default AnalystsWidget;
